import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { API_ROOT } from '../service/Services';
import CountUp from 'react-countup';
import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

export const DataVisual = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_ROOT}/data-visual-report`)
      .then(res => {
        setReport(res.data);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const categoryBarData = useMemo(() => {
    return (report?.stockByCategory || []).map(c => ({
      category: c._id || 'Uncategorized',
      totalStock: Number(c.totalStock || 0),
      count: Number(c.count || 0)
    }));
  }, [report]);

  const lowStockBarData = useMemo(() => {
    return (report?.lowStockItems || []).slice(0, 10).map(i => ({
      name: i.itemName,
      current: Number(i.stock || 0),
      reorder: Number(i.reorderLevel || 0)
    }));
  }, [report]);

  const categoryPieData = useMemo(() => {
    const data = report?.stockByCategory || [];
    const total = data.reduce((s, d) => s + Number(d.totalStock || 0), 0) || 1;
    return data
      .map(d => ({
        name: d._id || 'Uncategorized',
        value: Number(d.totalStock || 0),
        percent: Math.round(((Number(d.totalStock || 0)) / total) * 100)
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [report]);

  // ✅ FIX: sum items[].quantity, not transaction.quantity
  const txSeriesByDay = useMemo(() => {
    const tx = report?.recentTransactions || [];
    const byDayMap = new Map();
    tx.forEach(t => {
      const day = new Date(t.createdAt).toISOString().slice(0,10);
      if (!byDayMap.has(day)) byDayMap.set(day, { day, Purchase: 0, Sale: 0 });
      const row = byDayMap.get(day);

      const qty = (t.items || []).reduce((s,i)=> s + Number(i.quantity||0), 0);
      const key = t.transactionType === 'Purchase' ? 'Purchase' : 'Sale';
      row[key] = (row[key] || 0) + qty;
    });
    return Array.from(byDayMap.values()).sort((a,b) => a.day.localeCompare(b.day));
  }, [report]);

  // ✅ FIX: also use items[].quantity for totals
  const insights = useMemo(() => {
    const lowCount = report?.lowStockItems?.length || 0;
    const categories = report?.stockByCategory?.length || 0;
    const totalItems = report?.totalItems || 0;
    const purchases = (report?.recentTransactions || []).filter(t => t.transactionType === 'Purchase');
    const sales = (report?.recentTransactions || []).filter(t => t.transactionType === 'Sale');
    
    const purchaseQty = purchases.reduce((s, t) => 
      s + (t.items || []).reduce((ss,i)=> ss + Number(i.quantity||0),0)
    , 0);

    const saleQty = sales.reduce((s, t) => 
      s + (t.items || []).reduce((ss,i)=> ss + Number(i.quantity||0),0)
    , 0);

    const lowRate = totalItems ? Math.round((lowCount / totalItems) * 100) : 0;
    const topCategory = categoryPieData[0]?.name || '—';
    return [
      `Low-stock rate is ${lowRate}% (${lowCount}/${totalItems}).`,
      `Top category by stock is ${topCategory}.`,
      `30-day movement: Purchased ${purchaseQty}, Sold ${saleQty}.`,
      `Total categories: ${categories}.`
    ];
  }, [report, categoryPieData]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (!report?.success) return <div className="p-6 text-red-600">Error loading report</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Inventory Analytics</h2>
        <p className="text-sm text-gray-500">Key metrics and visualizations for stock health</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: report.totalItems },
          { label: 'Total Stock', value: report.totalStock },
          { label: 'Low Stock Items', value: report.lowStockItems?.length },
          { label: 'Categories', value: report.stockByCategory?.length }
        ].map((card, idx) => (
          <div key={idx} className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">{card.label}</div>
            <div className="text-3xl font-bold"><CountUp end={card.value || 0} duration={1.2} /></div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-4 lg:col-span-2">
          <h3 className="font-semibold mb-2">Stock by Category</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RBarChart data={categoryBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" tick={{ fontSize: 12 }} interval={0} angle={-10} height={60} textAnchor="end" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="totalStock" fill="#0ea5e9" radius={[4,4,0,0]} />
              </RBarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Category Share</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie dataKey="value" data={categoryPieData} nameKey="name" outerRadius={80} innerRadius={45}>
                  {categoryPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={["#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#14b8a6","#eab308","#f43f5e"][index % 8]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.percent}%)`, p.payload.name]} />
                <Legend verticalAlign="bottom" height={24} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Items + Movement */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Top Items</h3>
          <ul className="divide-y">
            {(report.topItems || []).map(item => (
              <li key={item.itemCode} className="py-2 flex items-center justify-between">
                <span className="text-sm">{item.itemName}</span>
                <span className="text-sm font-medium">{item.stock}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-lg shadow p-4 lg:col-span-2">
          <h3 className="font-semibold mb-2">30-day Movement (Qty)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={txSeriesByDay}>
                <defs>
                  <linearGradient id="colorPurchase" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSale" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="Purchase" stroke="#10b981" fillOpacity={1} fill="url(#colorPurchase)" />
                <Area type="monotone" dataKey="Sale" stroke="#ef4444" fillOpacity={1} fill="url(#colorSale)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold mb-2">Insights</h3>
        <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
          {insights.map((tip, idx) => (
            <li key={idx}>{tip}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
